import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal, Table } from 'react-bootstrap';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, formatNumber } from '../ui';

const EMPTY = { name: '', contact_person: '', phone: '', email: '', address: '', notes: '' };
const copy = PHARMACY_COPY.SUPPLIERS;

export default function PharmacySuppliersPage() {
  const { toast } = usePharmacy();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pharmacyService.listSuppliers({
        search: search || undefined,
        is_active:
          statusFilter === 'active' ? 'true' : statusFilter === 'inactive' ? 'false' : undefined
      });
      setRows(res.success ? res.data || [] : []);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.is_active).length;
    return { total: rows.length, active, inactive: rows.length - active };
  }, [rows]);

  const openNew = () => {
    setForm(EMPTY);
    setEditing('new');
  };

  const openEdit = (s) => {
    setForm({
      name: s.name || '',
      contact_person: s.contact_person || '',
      phone: s.phone || '',
      email: s.email || '',
      address: s.address || '',
      notes: s.notes || ''
    });
    setEditing(s.id);
  };

  const closeModal = () => {
    if (busy) return;
    setEditing(null);
  };

  const patchForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        contact_person: form.contact_person.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null
      };
      const res =
        editing === 'new'
          ? await pharmacyService.createSupplier(payload)
          : await pharmacyService.updateSupplier(editing, payload);
      if (res.success) {
        toast('success', res.message || copy.SAVE_OK);
        setEditing(null);
        await load();
      } else {
        toast('danger', res.message || copy.SAVE_FAIL);
      }
    } catch (e) {
      toast('danger', e.message || copy.SAVE_FAIL);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      const res = await pharmacyService.updateSupplier(s.id, { is_active: !s.is_active });
      if (res.success) {
        toast('success', s.is_active ? 'Fournisseur désactivé.' : 'Fournisseur activé.');
        await load();
      } else {
        toast('danger', res.message || copy.SAVE_FAIL);
      }
    } catch (e) {
      toast('danger', e.message || copy.SAVE_FAIL);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(copy.DELETE_CONFIRM.replace('{name}', s.name))) return;
    try {
      const res = await pharmacyService.deleteSupplier(s.id);
      if (res.success) {
        toast('success', copy.DELETE_OK);
        await load();
      } else {
        toast('danger', res.message || copy.DELETE_FAIL);
      }
    } catch (e) {
      toast('danger', e.message || copy.DELETE_FAIL);
    }
  };

  const canSave = form.name.trim() && !busy;
  const isNew = editing === 'new';

  return (
    <div className="cp-suppliers-page">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.TITLE}
        description={copy.DESC}
        actions={
          <button type="button" className="cp-btn cp-btn--primary" onClick={openNew}>
            <Icon name="plus" size={16} aria-hidden />
            {copy.NEW_BUTTON}
          </button>
        }
      />

      <section className="cp-catalog-stats" aria-label="Synthèse fournisseurs">
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.STAT_TOTAL}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.total)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--ok">
          <span className="cp-catalog-stat__label">{copy.STAT_ACTIVE}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.active)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--muted">
          <span className="cp-catalog-stat__label">{copy.STAT_INACTIVE}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.inactive)}</span>
        </article>
      </section>

      <div className="cp-card">
        <div className="cp-catalog-toolbar">
          <div className="cp-search-wrap">
            <Icon name="search" size={16} className="cp-search-wrap__icon" aria-hidden />
            <input
              type="search"
              className="cp-search-wrap__input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_ARIA}
            />
          </div>
          <div className="cp-catalog-toolbar__end">
            <div className="cp-filter-pills" role="group" aria-label="Statut">
              {[
                { value: 'all', label: copy.FILTER_ALL },
                { value: 'active', label: copy.FILTER_ACTIVE },
                { value: 'inactive', label: copy.FILTER_INACTIVE }
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`cp-filter-pill${statusFilter === opt.value ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="cp-icon-btn"
              onClick={load}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={17} aria-hidden />
            </button>
            <div className="cp-catalog-meta">
              <strong>{loading ? '—' : formatNumber(stats.total)}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        <div className="cp-card__body cp-card__body--flush">
          {loading ? (
            <LoadingBlock label="Chargement des fournisseurs…" />
          ) : rows.length === 0 ? (
            <div className="cp-empty-state">
              <span className="cp-empty-state__icon" aria-hidden>
                <Icon name="truck" size={28} />
              </span>
              <h3 className="cp-empty-state__title">{copy.EMPTY_TITLE}</h3>
              <p className="cp-empty-state__text">{copy.EMPTY_HINT}</p>
              <button type="button" className="cp-btn cp-btn--primary" onClick={openNew}>
                <Icon name="plus" size={16} aria-hidden />
                {copy.EMPTY_CTA}
              </button>
            </div>
          ) : (
            <div className="cp-table-wrap">
              <Table responsive className="cp-table mb-0">
                <thead>
                  <tr>
                    <th>{copy.COL_NAME}</th>
                    <th>{copy.COL_CONTACT}</th>
                    <th>{copy.COL_PHONE}</th>
                    <th>{copy.COL_EMAIL}</th>
                    <th>{copy.COL_STATUS}</th>
                    <th className="text-end">{copy.COL_ACTIONS}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.id} className={editing === s.id ? 'is-editing' : undefined}>
                      <td>
                        <div className="cp-med-cell">
                          <span className="cp-med-cell__icon" aria-hidden>
                            <Icon name="truck" size={16} />
                          </span>
                          <span className="cp-med-cell__body">
                            <span className="cp-med-cell__name">{s.name}</span>
                            {s.address ? <span className="cp-med-cell__desc">{s.address}</span> : null}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="cp-contact-cell">
                          {s.contact_person ? (
                            <>
                              <Icon name="user" size={13} aria-hidden />
                              {s.contact_person}
                            </>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </span>
                      </td>
                      <td>
                        {s.phone ? (
                          <span className="cp-contact-cell">
                            <Icon name="phone" size={13} aria-hidden />
                            {s.phone}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {s.email ? (
                          <span className="cp-contact-cell">
                            <Icon name="envelope" size={13} aria-hidden />
                            {s.email}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`cp-status-pill ${
                            s.is_active ? 'cp-status-pill--ok' : 'cp-status-pill--muted'
                          }`}
                        >
                          {s.is_active ? copy.STATUS_ACTIVE : copy.STATUS_INACTIVE}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="cp-row-actions">
                          <button
                            type="button"
                            className="cp-btn cp-btn--ghost cp-btn--sm"
                            onClick={() => openEdit(s)}
                          >
                            <Icon name="edit" size={13} aria-hidden />
                            {copy.EDIT}
                          </button>
                          <button
                            type="button"
                            className="cp-btn cp-btn--ghost cp-btn--sm"
                            onClick={() => toggleActive(s)}
                          >
                            {s.is_active ? copy.DEACTIVATE : copy.ACTIVATE}
                          </button>
                          <button
                            type="button"
                            className="cp-btn cp-btn--danger cp-btn--sm"
                            onClick={() => remove(s)}
                            title={copy.DELETE}
                          >
                            <Icon name="trash" size={13} aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={!!editing}
        onHide={closeModal}
        centered
        size="lg"
        className="cp-modal"
        backdrop={busy ? 'static' : true}
        keyboard={!busy}
      >
        <Modal.Header closeButton={!busy} className="cp-modal__header">
          <div className="cp-modal__intro">
            <span className="cp-modal__badge" aria-hidden>
              <Icon name="truck" size={16} />
            </span>
            <div>
              <Modal.Title as="h2" className="cp-modal__title">
                {isNew ? copy.MODAL_NEW : copy.MODAL_EDIT}
              </Modal.Title>
              <p className="cp-modal__desc">{isNew ? copy.MODAL_NEW_DESC : copy.MODAL_EDIT_DESC}</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          <section className="cp-form-section">
            <h3 className="cp-form-section__title">{copy.SECTION_IDENTITY}</h3>
            <div className="cp-form-grid">
              <Form.Group className="cp-form-field cp-form-field--full">
                <Form.Label>{copy.FIELD_NAME} *</Form.Label>
                <Form.Control
                  autoFocus
                  value={form.name}
                  onChange={(e) => patchForm('name', e.target.value)}
                  placeholder="Ex. Pharma Distributors SARL"
                />
                <Form.Text>{copy.FIELD_NAME_HINT}</Form.Text>
              </Form.Group>
              <Form.Group className="cp-form-field cp-form-field--full">
                <Form.Label>{copy.FIELD_ADDRESS}</Form.Label>
                <Form.Control
                  value={form.address}
                  onChange={(e) => patchForm('address', e.target.value)}
                />
              </Form.Group>
              <Form.Group className="cp-form-field cp-form-field--full">
                <Form.Label>{copy.FIELD_NOTES}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => patchForm('notes', e.target.value)}
                />
              </Form.Group>
            </div>
          </section>
          <section className="cp-form-section">
            <h3 className="cp-form-section__title">{copy.SECTION_CONTACT}</h3>
            <div className="cp-form-grid">
              <Form.Group className="cp-form-field">
                <Form.Label>{copy.FIELD_CONTACT}</Form.Label>
                <Form.Control
                  value={form.contact_person}
                  onChange={(e) => patchForm('contact_person', e.target.value)}
                />
              </Form.Group>
              <Form.Group className="cp-form-field">
                <Form.Label>{copy.FIELD_PHONE}</Form.Label>
                <Form.Control
                  value={form.phone}
                  onChange={(e) => patchForm('phone', e.target.value)}
                />
              </Form.Group>
              <Form.Group className="cp-form-field cp-form-field--full">
                <Form.Label>{copy.FIELD_EMAIL}</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={(e) => patchForm('email', e.target.value)}
                />
              </Form.Group>
            </div>
          </section>
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <button type="button" className="cp-btn cp-btn--ghost" onClick={closeModal} disabled={busy}>
            {copy.CANCEL}
          </button>
          <button type="button" className="cp-btn cp-btn--primary" onClick={save} disabled={!canSave}>
            {busy ? copy.SAVING : copy.SAVE}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
